import React, { useEffect, useState } from "react";
import { MessageCircle, Send, Clock, Info, Loader2, Crown } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * SMS Blast panel — Premium Creator + Admin only.
 * Sends a 160-char SMS to followers with a phone number who opted into SMS
 * marketing. Rate-limited to 1 send per 24h for premium creators (unlimited
 * for admin). Requires Twilio env keys on the backend — returns 503 until set.
 */
export default function SmsBlastPanel({ user }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");

  const isEligible = user?.role === "premium_creator" || user?.role === "admin";

  const loadHistory = async () => {
    try { const { data } = await api.get("/creator/sms-blast/history"); setHistory(data); }
    catch {}
  };

  useEffect(() => { if (isEligible) loadHistory(); }, [isEligible]);

  const send = async () => {
    if (!message.trim()) return toast.error("Message required");
    if (!window.confirm(`Send this text to every opted-in follower with a phone number?`)) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/creator/sms-blast", { message: message.trim() });
      toast.success(`Sent to ${data.sent} · ${data.failed} failed · ${data.opted_in} opted-in`);
      setMessage("");
      loadHistory();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 503) {
        msg = "SMS provider not configured — ask admin to add Twilio keys.";
      } else if (status === 429) {
        msg = detail || "SMS blasts are limited to 1 per 24 hours.";
      } else {
        msg = formatApiError(e);
      }
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!isEligible) {
    return (
      <section data-testid="sms-blast-panel" className="tv-glass rounded-2xl border border-white/10 p-6 max-w-3xl">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">
          <Crown className="w-4 h-4" /> Premium creator feature
        </div>
        <h3 className="font-display font-black text-2xl text-white tracking-tight mt-1">SMS Blasts are Premium</h3>
        <p className="text-white/60 text-sm mt-2">
          Upgrade to a Premium Creator plan to text your followers directly when you drop, go live, or launch merch.
        </p>
      </section>
    );
  }

  return (
    <section data-testid="sms-blast-panel" className="tv-glass rounded-2xl border border-white/10 p-6 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2">
        <MessageCircle className="w-4 h-4" /> SMS blast to followers
      </div>
      <h3 className="font-display font-black text-2xl text-white tracking-tight mt-1">Text them directly</h3>
      <p className="text-white/60 text-sm mt-2">
        We'll text every follower who opted into SMS marketing and added a phone number. {user?.role === "admin" ? (
          <span className="text-emerald-300 font-semibold">Admin: unlimited sends.</span>
        ) : (
          <span className="text-yellow-300 font-semibold">Premium creators: 1 blast per 24 hours.</span>
        )}
      </p>

      <div className="mt-5 space-y-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold flex items-center justify-between">
            <span>Message</span>
            <span className="text-white/40">{message.length}/160</span>
          </span>
          <textarea
            data-testid="sms-blast-message"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 160))}
            maxLength={160}
            rows={3}
            placeholder="Going live in 20 min — tap in! tapintv.com/live"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent resize-none"
          />
          <div className="text-[10px] text-white/40 mt-1">We prefix your name automatically. Keep it short — carriers split anything over 160 chars.</div>
        </label>
      </div>

      <div className="mt-4 flex gap-2 items-start text-xs text-white/60 bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-tv-accent" />
        <div>SMS requires Twilio keys on the backend. If you see a 503 error, ask admin to configure Twilio env variables. Blast also shows up as an in-app notification.</div>
      </div>

      {err && (
        <div className="mt-3 text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="sms-blast-error">
          {err}
        </div>
      )}

      <button
        data-testid="sms-blast-send-btn"
        onClick={send}
        disabled={busy || !message.trim()}
        className="mt-5 bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-bold px-6 py-3 rounded-full flex items-center gap-2 text-sm"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {busy ? "Sending…" : "Send text blast"}
      </button>

      {history.length > 0 && (
        <div className="mt-8">
          <div className="text-xs uppercase tracking-wider text-white/50 font-bold mb-2 flex items-center gap-2"><Clock className="w-3 h-3" /> Recent SMS blasts</div>
          <div className="space-y-2" data-testid="sms-blast-history">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{h.message}</div>
                  <div className="text-[11px] text-white/50">
                    {new Date(h.sent_at).toLocaleString()} · {h.recipients_count} delivered{h.failed_count ? ` · ${h.failed_count} failed` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
